import { MatSnackBar } from '@angular/material/snack-bar'
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';

import { HttpService } from '../../../api/http.service'
import { manageSubscriptionActions } from './manage-subscription.actions'
import { catchError, concatMap, mergeMap, of, switchMap } from 'rxjs'
import { Injectable } from '@angular/core'
import { ErrorSnackBarComponent } from '../../../utils/components/error-snack-bar/error-snack-bar.component'
import { SuccessSnackBarComponent } from '../../../utils/components/success-snack-bar/success-snack-bar.component'
import { selectManageSubscription, selectUserData, UserData } from './manage-subscription.state'
import { Store } from '@ngrx/store'
import { TranslateService } from '@ngx-translate/core'

@Injectable()
export class ManageSubscriptionEffects {
  constructor(
    private readonly actions$: Actions,
    private readonly store: Store,
    private readonly httpService: HttpService,
    private readonly snackbar: MatSnackBar,
    private readonly translate: TranslateService,
  ) {}

  $loadUserData = createEffect(() => {
    return this.actions$.pipe(
      ofType(manageSubscriptionActions.tryLoadData),
      switchMap(action =>
        this.httpService.getCustomFieldValues().pipe(
          switchMap(customFields =>
            this.httpService.getUserInfoById(action.payload.userId).pipe(
              mergeMap(userDataResponse =>
                of(
                  manageSubscriptionActions.successLoadCustomFields({ payload: customFields }),
                  manageSubscriptionActions.successLoadData({ payload: userDataResponse }),
                ),
              ),
              catchError(error => {
                // this.snackbar.openFromComponent(ErrorSnackBarComponent, {
                //   duration: 5000,
                //   panelClass: ['error-snack-bar'],
                //   data: { title: error.message },
                // })
                return of(manageSubscriptionActions.errorLoadData({ payload: error.message }))
              }),
            ),
          ),
          catchError(error => {
            this.snackbar.openFromComponent(ErrorSnackBarComponent, {
              duration: 5000,
              panelClass: ['error-snack-bar'],
              data: { title: error.message },
            })
            return of(manageSubscriptionActions.errorLoadCustomFields({ payload: error.message }))
          }),
        ),
      ),
    )
  })

  $saveUserPref = createEffect(() => {
    return this.actions$.pipe(
      ofType(manageSubscriptionActions.trySaveUserPref),
      concatLatestFrom(() => this.store.select(selectManageSubscription)),
      switchMap(([action, { data, controls, customFields }]) => {
        const userFields = data.customFieldValues.filter(c => !controls.isSelected.find(i => i.id === c.customFieldId))
        const subscriptionFields = customFields
          .filter(c => controls.isSelected.find(i => i.id === c.customFieldId)?.isSelected)
          .map(c => {
            const newValues = c.values.filter(value =>
              controls.isSelected
                .find(i => i.id === c.customFieldId)
                ?.themenSelected.filter(t => t.isSelected)
                .map(t => t.id)
                .includes(value),
            )

            return {
              ...c,
              value: newValues,
              values: newValues,
            }
          })

        const newUserData: UserData = {
          ...data,
          customFieldValues: [...userFields, ...subscriptionFields],
        }

        return this.httpService.saveUserPref(newUserData.contactId, action.payload.unsubscribeCover, newUserData).pipe(
          switchMap(() => {
            this.snackbar.openFromComponent(SuccessSnackBarComponent, {
              panelClass: ['success-snack-bar'],
              data: { title: this.translate.instant('ACCOUNT.MANAGE_SUBSCRIPTION.SNACKS.SAVE_SUCCESS_INFO') },
            })
            return of(manageSubscriptionActions.saveUserPrefSuccess({ payload: undefined }))
          }),
          catchError(e => {
            this.snackbar.openFromComponent(ErrorSnackBarComponent, {
              duration: 5000,
              panelClass: ['error-snack-bar'],
              data: { title: this.translate.instant('ACCOUNT.MANAGE_SUBSCRIPTION.SNACKS.SAVE_ERROR') },
            })
            return of(manageSubscriptionActions.saveUserPrefError({ payload: e.message }))
          }),
        )
      }),
    )
  })

  $reloadUserData = createEffect(() => {
    return this.actions$.pipe(
      ofType(manageSubscriptionActions.saveUserPrefSuccess),
      concatLatestFrom(() => this.store.select(selectUserData)),
      switchMap(([_, userData]) =>
        of(manageSubscriptionActions.tryLoadData({ payload: { userId: userData.contactId } })),
      ),
    )
  })

  $unsubscribe = createEffect(() => {
    return this.actions$.pipe(
      ofType(manageSubscriptionActions.unsubscribe),
      concatMap(() => [
        manageSubscriptionActions.unmarkUserDoi({ payload: undefined }),
        manageSubscriptionActions.unmarkAll({ payload: undefined }),
        manageSubscriptionActions.trySaveUserPref({ payload: { unsubscribeCover: true } }),
      ]),
    )
  })
}
